import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/layout/Logo";
import { registerGlobalAlert } from "@/lib/dialogs/globalErrorBridge";

type DialogKind = "alert" | "confirm" | "prompt";

interface DialogOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  inputType?: string;
  value?: string;
  placeholder?: string;
  inputLabel?: string;
}

type ResolveValue = boolean | string | null;

interface QueueItem {
  id: number;
  kind: DialogKind;
  message: string;
  options: DialogOptions;
  resolve: (value: ResolveValue) => void;
}

interface DialogContextValue {
  alert: (message: string, options?: DialogOptions) => Promise<true>;
  confirm: (message: string, options?: DialogOptions) => Promise<boolean>;
  prompt: (message: string, options?: DialogOptions) => Promise<string | null>;
}

const DEFAULTS: Record<DialogKind, { title: string; confirmLabel: string; cancelLabel?: string }> = {
  alert: { title: "Hinweis", confirmLabel: "Verstanden" },
  confirm: { title: "Bitte bestätigen", confirmLabel: "Bestätigen", cancelLabel: "Abbrechen" },
  prompt: { title: "Eingabe", confirmLabel: "Übernehmen", cancelLabel: "Abbrechen" }
};

function cancelValueFor(kind: DialogKind): ResolveValue {
  if (kind === "confirm") return false;
  if (kind === "prompt") return null;
  return true;
}

const DialogQueueContext = createContext<DialogContextValue | null>(null);

let sequence = 0;

export function DialogProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [promptValue, setPromptValue] = useState("");
  const current = queue[0] ?? null;

  const enqueue = useCallback((kind: DialogKind, message: string, options: DialogOptions = {}) => {
    return new Promise<ResolveValue>((resolve) => {
      const item: QueueItem = { id: ++sequence, kind, message, options, resolve };
      setQueue((prev) => {
        const next = [...prev, item];
        if (next.length === 1 && kind === "prompt") setPromptValue(options.value ?? "");
        return next;
      });
    });
  }, []);

  const settle = useCallback((item: QueueItem, value: ResolveValue) => {
    setQueue((prev) => {
      if (prev[0]?.id !== item.id) return prev;
      item.resolve(value);
      const next = prev.slice(1);
      if (next[0]?.kind === "prompt") setPromptValue(next[0].options.value ?? "");
      return next;
    });
  }, []);

  useEffect(() => {
    registerGlobalAlert((message, options) => enqueue("alert", message, options) as Promise<true>);
    return () => registerGlobalAlert(null);
  }, [enqueue]);

  const value = useMemo<DialogContextValue>(
    () => ({
      alert: (message, options) => enqueue("alert", message, options) as Promise<true>,
      confirm: (message, options) => enqueue("confirm", message, options) as Promise<boolean>,
      prompt: (message, options) => enqueue("prompt", message, options) as Promise<string | null>
    }),
    [enqueue]
  );

  const alertOrConfirmOpen = !!current && current.kind !== "prompt";
  const promptOpen = !!current && current.kind === "prompt";

  return (
    <DialogQueueContext.Provider value={value}>
      {children}

      <AlertDialog
        open={alertOrConfirmOpen}
        onOpenChange={(open) => {
          if (!open && current) settle(current, cancelValueFor(current.kind));
        }}
      >
        {current && current.kind !== "prompt" ? (
          <AlertDialogContent>
            <DialogBrandHeader />
            <AlertDialogHeader>
              <AlertDialogTitle>{current.options.title || DEFAULTS[current.kind].title}</AlertDialogTitle>
              <AlertDialogDescription className="whitespace-pre-wrap wrap-anywhere text-left">
                {current.message}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {current.kind === "confirm" ? (
                <AlertDialogCancel onClick={() => settle(current, false)}>
                  {current.options.cancelLabel || DEFAULTS.confirm.cancelLabel}
                </AlertDialogCancel>
              ) : null}
              <AlertDialogAction
                variant={current.options.danger ? "destructive" : "default"}
                onClick={() => settle(current, true)}
              >
                {current.options.confirmLabel || DEFAULTS[current.kind].confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        ) : null}
      </AlertDialog>

      <Dialog
        open={promptOpen}
        onOpenChange={(open) => {
          if (!open && current) settle(current, cancelValueFor(current.kind));
        }}
      >
        {current && current.kind === "prompt" ? (
          <DialogContent>
            <DialogBrandHeader />
            <form
              onSubmit={(event) => {
                event.preventDefault();
                settle(current, promptValue);
              }}
            >
              <DialogHeader>
                <DialogTitle>{current.options.title || DEFAULTS.prompt.title}</DialogTitle>
                <DialogDescription className="whitespace-pre-wrap wrap-anywhere text-left">
                  {current.message}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <Label htmlFor="cf-dialog-prompt-input" className="sr-only">
                  {current.options.inputLabel || current.options.title || DEFAULTS.prompt.title}
                </Label>
                <Input
                  id="cf-dialog-prompt-input"
                  autoFocus
                  type={current.options.inputType || "text"}
                  placeholder={current.options.placeholder}
                  value={promptValue}
                  onChange={(event) => setPromptValue(event.target.value)}
                />
              </div>
              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => settle(current, null)}>
                  {current.options.cancelLabel || DEFAULTS.prompt.cancelLabel}
                </Button>
                <Button type="submit">{current.options.confirmLabel || DEFAULTS.prompt.confirmLabel}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        ) : null}
      </Dialog>
    </DialogQueueContext.Provider>
  );
}

function DialogBrandHeader() {
  return (
    <div className="-mx-4 -mt-3 mb-2 flex items-center justify-center rounded-t-xl border-b bg-muted/50 px-4 py-3">
      <Logo className="h-10" />
    </div>
  );
}

export function useDialog(): DialogContextValue {
  const context = useContext(DialogQueueContext);
  if (!context) {
    throw new Error("useDialog() muss innerhalb von <DialogProvider> aufgerufen werden.");
  }
  return context;
}
